/**
 * SigningEntryPage — Entry point from signing-notification emails.
 *
 * Hardened verification flow:
 *  - Defensive URL-param checks up front
 *  - Soft fallback ("We couldn't verify this signing link…") instead of a
 *    blocking "Invalid Link" wall
 *  - Backend `detail` surfaced verbatim where useful
 *  - Diagnostic console logs (non-sensitive) gated to dev or `?debug=1`
 *  - Idempotent: if user has already signed, routes them forward sensibly
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USE_V2_API = process.env.REACT_APP_USE_V2_API === 'true';
const API_V2 = `${BACKEND_URL}/api/v2`;

const SOFT_ERR =
  "We couldn't verify this signing link. Please request a fresh link or return to your MarrsingClause® experience.";

const diag = (...args) => {
  try {
    const isDev = process.env.NODE_ENV !== 'production';
    const debugFlag = typeof window !== 'undefined' && window.location.search.includes('debug=1');
    if (isDev || debugFlag) {
      // eslint-disable-next-line no-console
      console.log('[SigningEntry]', ...args);
    }
  } catch { /* noop */ }
};

const SigningEntryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const relationshipId = searchParams.get('relationship_id');
  const userId = searchParams.get('user_id');

  useEffect(() => {
    diag('mount', {
      relationship_id_present: !!relationshipId,
      user_id_present: !!userId,
      api_base: API,
      v2_enabled: USE_V2_API,
      env_origin: typeof window !== 'undefined' ? window.location.origin : null,
    });

    if (!relationshipId || !userId) {
      setError(SOFT_ERR);
      setLoading(false);
      return;
    }

    sessionStorage.setItem('relationship_id', relationshipId);
    sessionStorage.setItem('user_id', userId);

    const verifyAndRedirect = async () => {
      try {
        const url = USE_V2_API
          ? `${API_V2}/relationships/${relationshipId}`
          : `${API}/relationships/${relationshipId}`;
        diag('GET', url);
        const response = await axios.get(url);
        diag('GET status', response.status);
        const { partners, state } = response.data;

        const currentPartner = partners?.find((p) => p.user_id === userId);
        if (!currentPartner) {
          diag('user not found in partners', { partners_count: partners?.length || 0 });
          setError(SOFT_ERR);
          setLoading(false);
          return;
        }

        diag('matched partner', {
          role: currentPartner.role,
          already_signed: !!currentPartner.signed_at,
          both_signed: !!state?.both_signed,
        });

        if (response.data.relationship?.selected_level) {
          sessionStorage.setItem('selected_level', response.data.relationship.selected_level);
        }

        if (currentPartner.signed_at) {
          toast.info('You have already signed.');
          if (state?.both_signed) {
            navigate(`/reveal/${relationshipId}`);
          } else {
            navigate('/waiting');
          }
          return;
        }

        navigate('/signature');
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        diag('verify failed', { status, detail });
        // 404 / 403 / 400 = link is wrong env or stale → friendly soft fallback
        // anything else (network, 5xx) = surface detail so we can debug
        if (status === 404 || status === 403 || status === 400) {
          setError(SOFT_ERR);
        } else if (typeof detail === 'string' && detail.length > 0) {
          setError(detail);
        } else {
          setError(SOFT_ERR);
        }
        setLoading(false);
      }
    };

    verifyAndRedirect();
  }, [relationshipId, userId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-stone-900 mx-auto mb-4" />
            <p className="text-stone-600">Verifying your signing link…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{ minHeight: 'calc(100vh - 100px)' }}>
          <div
            className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-10 text-center"
            data-testid="signing-entry-soft-error"
          >
            <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-7 h-7 text-stone-600" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">
              We couldn't verify this signing link
            </h2>
            <p className="text-stone-600 leading-7 mb-8">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-7 py-3 text-base font-medium transition-all"
              data-testid="signing-entry-home-btn"
            >
              Return to MarrsingClause®
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SigningEntryPage;
